import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  Box, Checkbox, Menu, MenuItem, Table, TableBody, TableCell, TableHead, TableRow,
  Tooltip, Typography, IconButton, ListItemText, ListItemIcon, Divider,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CheckIcon from '@mui/icons-material/Check';
import TuneIcon from '@mui/icons-material/Tune';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import BarChartIcon from '@mui/icons-material/BarChart';
import {
  FaHourglassHalf, FaClock, FaCircle, FaFlag, FaFolder,
} from 'react-icons/fa';
import {
  CardWrapper, SearchField, formatBRL,
} from '../UI';
import AcoesEmMassaMenu from './AcoesEmMassa/AcoesEmMassaMenu';
import {
  AlertaVencimentoProcesso, AlertaCompromissoProcesso, AlertaProcessoNaoTrabalhado,
  AlertaComRequisicao, AlertaPasta,
} from './AlertasProcesso';
import ResizeHandle from './ResizeHandle';
import {
  ORDENACAO_OPTIONS,
} from '../../constants/processos.constants';
import { changeKeyHandler, KEY_NUMBER } from '../../../helpers/utils/changeEvents.utils';
import * as C from '../../theme/colors.constants';

/**
 * Grid de Processos com largura ajustável.
 *
 * Requisito: "opção para o usuário clicar na lateral direita e conseguir
 * aumentar para exibir mais colunas ou diminuir para ocultar as colunas".
 *
 * Como resolvi:
 * - A grid vive num painel cuja largura é estado (`largura`, em px).
 * - Cada coluna declara `min` (largura mínima) e `fixa` (nunca some).
 * - `colunasVisiveis` reserva primeiro a soma das colunas `fixa`
 *   (`SOMA_MIN_FIXAS`, calculada uma única vez) e só depois percorre as
 *   opcionais na ordem do array, somando `min` contra o orçamento restante —
 *   assim uma opcional posicionada antes de uma fixa não entra "de graça"
 *   contra um orçamento que ainda não descontou o peso da fixa. Arrastar para
 *   a direita revela colunas progressivamente e arrastar para a esquerda as
 *   oculta — sem nenhum menu de configuração.
 * - O arraste é feito por `ResizeHandle`, que ouve mousemove/mouseup no
 *   `document` (não no elemento) para o cursor não "escapar" durante o drag.
 *
 * As colunas Checkbox (necessária para Ações em Massa), Processo, Valor
 * Principal e a ampulheta (`corAlertaFase`, cujo hint de cabeçalho é "Dias
 * com Processo") são `fixa`, então o painel nunca chega a um estado
 * ilegível — na largura mínima, só essas 4 aparecem.
 */

// Soma das 4 colunas fixas (44+86+118+56=304). LARGURA_MIN precisa ficar
// abaixo de 304+FOLGA+38 (o menor `min` opcional, um ícone) para garantir que
// nenhuma coluna opcional caiba no orçamento restante na largura mínima —
// ver `colunasVisiveis` abaixo, que soma as fixas antes de avaliar as
// opcionais, então esse cálculo independe da posição delas no array.
const LARGURA_MIN = 400;
const LARGURA_MAX_FALLBACK = 1700;

/** Definição das colunas, na ordem de revelação. */
const COLUNAS = [
  {
    id: 'checkbox', label: '', min: 44, fixa: true, align: 'center',
  },
  {
    id: 'nroProcesso', label: 'Processo', min: 86, fixa: true, align: 'left', ordenavel: true,
  },
  {
    id: 'naoTrabalhado', label: <FaCircle size={9} />, hint: 'Processo não trabalhado', min: 38, align: 'center',
  },
  {
    id: 'compromisso', label: <FaClock size={13} />, hint: 'Compromisso', min: 38, align: 'center',
  },
  {
    id: 'requisicao', label: <FaFlag size={12} />, hint: 'Com requisição', min: 38, align: 'center',
  },
  {
    id: 'pasta', label: <FaFolder size={12} />, hint: 'Pasta', min: 38, align: 'center',
  },
  {
    id: 'valorPrincipal', label: 'Valor Principal', min: 118, fixa: true, align: 'right', ordenavel: true,
  },
  {
    id: 'corAlertaFase', label: <FaHourglassHalf size={13} />, hint: 'Dias com Processo', min: 56, fixa: true, align: 'center', ordenavel: true,
  },
  {
    id: 'status', label: 'Status', min: 96, align: 'left', ordenavel: true,
  },
];

const SOMA_MIN_FIXAS = COLUNAS.reduce((acc, c) => (c.fixa ? acc + c.min : acc), 0);

/**
 * Extrai o valor comparável de cada `ordenarPor` — só precisa de entrada
 * aqui quando o campo usado para ordenar não é o mesmo exibido na coluna
 * (ex.: `corAlertaFase` renderiza um código de letra, mas ordena pelo
 * número de dias real). Sem entrada, cai no fallback `p[ordenarPor]`.
 */
const EXTRATORES_ORDENACAO = {
  nroProcesso: p => Number(p.nroProcesso),
  corAlertaFase: p => p.diasFase ?? 0,
};

/** Botão de filtro com menu (Status / Condição / Ordenar). */
function FiltroMenu({
  label, valor, options, onChange, icone, multiplo = false, extra,
}) {
  const [anchor, setAnchor] = useState(null);
  const selecionados = multiplo ? valor || [] : [];

  const escolher = (opt) => {
    if (multiplo) {
      const novo = selecionados.includes(opt)
        ? selecionados.filter(x => x !== opt)
        : [...selecionados, opt];
      onChange(novo);
      return;
    }
    onChange(opt === valor ? '' : opt);
    setAnchor(null);
  };

  // eslint-disable-next-line no-nested-ternary
  const resumo = multiplo
    ? selecionados.length === 0 ? '' : `${selecionados.length} selec.`
    : valor || '';

  return (
    <>
      <Box
        onClick={e => setAnchor(e.currentTarget)}
        role="button"
        tabIndex={0}
        onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && setAnchor(e.currentTarget)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          cursor: 'pointer',
          border: `1px solid ${C.borderGrey}`,
          borderRadius: 2,
          backgroundColor: C.white,
          pl: 1.25,
          pr: 0.25,
          py: 0.5,
          minWidth: 96,
          flexShrink: 0,
          '&:hover': { borderColor: C.navy },
        }}
      >
        {icone}
        <Typography
          variant="body2"
          sx={{
            color: resumo ? C.black : C.greyish,
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontWeight: resumo ? 600 : 400,
          }}
        >
          {resumo || label}
        </Typography>
        <ArrowDropDownIcon sx={{ color: C.navy, fontSize: 20 }} />
      </Box>

      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        PaperProps={{ sx: { maxHeight: 420, minWidth: 260, boxShadow: C.shadowFloat } }}
      >
        <MenuItem disabled sx={{ opacity: '1 !important' }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: C.warmGrey, letterSpacing: 0.4 }}>
            {label.toUpperCase()}
          </Typography>
        </MenuItem>
        <Divider />
        {extra}
        {options.map((opt) => {
          const marcado = multiplo ? selecionados.includes(opt.id ?? opt) : (opt.id ?? opt) === valor;
          const texto = opt.label ?? opt;
          return (
            <MenuItem key={opt.id ?? opt} onClick={() => escolher(opt.id ?? opt)} selected={marcado}>
              <ListItemIcon sx={{ minWidth: 30 }}>
                {marcado ? <CheckIcon fontSize="small" sx={{ color: C.navy }} /> : null}
              </ListItemIcon>
              <ListItemText primaryTypographyProps={{ variant: 'body2' }}>{texto}</ListItemText>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}
FiltroMenu.propTypes = {
  label: PropTypes.string.isRequired,
  valor: PropTypes.any,
  options: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  icone: PropTypes.node,
  multiplo: PropTypes.bool,
  extra: PropTypes.node,
};
FiltroMenu.defaultProps = {
  valor: null,
  icone: null,
  multiplo: false,
  extra: null,
};

export default function GridProcessos({
  selecionado, onSelecionar, processos, statusOptions, condicaoOptions,
  onBuscarPorNumero, buscandoProcesso,
}) {
  const [largura, setLargura] = useState(LARGURA_MIN);
  const [nroProcesso, setNroProcesso] = useState('');
  const [status, setStatus] = useState('');
  const [condicao, setCondicao] = useState([]);
  const [ordenarPor, setOrdenarPor] = useState('nroProcesso');
  const [ordemAsc, setOrdemAsc] = useState(true);
  const [marcados, setMarcados] = useState([]);
  const wrapperRef = useRef(null);
  const [larguraMax, setLarguraMax] = useState(LARGURA_MAX_FALLBACK);

  // O painel não deve passar de ~78% da viewport, senão o painel de detalhe
  // desaparece. Recalculo no resize para acompanhar telas maiores.
  useEffect(() => {
    const calc = () => setLarguraMax(Math.max(LARGURA_MIN + 200, Math.round(window.innerWidth * 0.78)));
    calc();
    window.addEventListener('resize', calc);
    return () => window.removeEventListener('resize', calc);
  }, []);

  // Folga descontada da largura útil: padding horizontal do card (2x até 30px)
  // + a faixa da alça de resize. Sem isso a última coluna "entra" e trunca o
  // próprio cabeçalho.
  const FOLGA = 78;

  const colunasVisiveis = useMemo(() => {
    const orcamento = largura - FOLGA;
    let acumulado = SOMA_MIN_FIXAS;
    return COLUNAS.filter((c) => {
      if (c.fixa) return true;
      if (acumulado + c.min <= orcamento) {
        acumulado += c.min;
        return true;
      }
      return false;
    });
  }, [largura]);

  const dados = useMemo(() => {
    let lista = [...processos];
    if (status) lista = lista.filter(p => p.status === status);
    if (condicao.length) lista = lista.filter(p => condicao.includes(p.tituloAgendamento));

    const extrairValorOrdenacao = EXTRATORES_ORDENACAO[ordenarPor] ?? (p => p[ordenarPor]);
    lista.sort((a, b) => {
      const va = extrairValorOrdenacao(a);
      const vb = extrairValorOrdenacao(b);
      if (typeof va === 'string') return ordemAsc ? va.localeCompare(vb) : vb.localeCompare(va);
      return ordemAsc ? va - vb : vb - va;
    });
    return lista;
  }, [processos, status, condicao, ordenarPor, ordemAsc]);

  // Mesmo comportamento do campo "Nº Processo" de `HomePage.jsx`/
  // `PesquisaProcessosPage.jsx`: input restrito a dígitos, busca real
  // disparada só no Enter/clique da lupa (nunca a cada tecla) — substitui o
  // filtro local instantâneo que este campo tinha antes. O dispatch Redux
  // (`setResultadoAction`) e o Snackbar de erro ficam no container
  // (`ControleProcessosPage.jsx`, via `onBuscarPorNumero`/`buscandoProcesso`)
  // — este componente é de apresentação, não deve ler/escrever Redux direto
  // (mesma regra já seguida por `statusOptions`/`condicaoOptions` acima).
  //
  // Decisão explícita do usuário (não é um bug pendente): uma busca
  // encontrada SUBSTITUI toda a lista `processos` atual por um array de 1
  // item (mesmo mecanismo global de `resultado` que a Home/Pesquisa usam) —
  // não preserva/filtra dentro da lista já carregada. Sem caminho de volta
  // pelo próprio campo; o usuário refaz a pesquisa em
  // `PesquisaProcessosPage.jsx` para recuperar a lista anterior.
  const changeNroProcesso = (e) => {
    changeKeyHandler(e, KEY_NUMBER);
    setNroProcesso(e.target.value);
  };

  // `onBuscarPorNumero` retorna (via Promise) se o processo foi encontrado —
  // só assim para limpar o campo em caso de sucesso (paridade com a
  // navegação da Home, que "limpa" o campo ao sair da página).
  const buscarPorNumero = async () => {
    const encontrado = await onBuscarPorNumero(nroProcesso);
    if (encontrado) {
      setNroProcesso('');
    }
  };

  const alternarTodos = useCallback(
    e => setMarcados(e.target.checked ? dados.map(d => d.id) : []),
    [dados],
  );

  const renderCelula = (p, col) => {
    switch (col.id) {
      case 'checkbox':
        return (
          <Checkbox
            size="small"
            checked={marcados.includes(p.id)}
            onClick={e => e.stopPropagation()}
            onChange={e => setMarcados(m => (e.target.checked ? [...m, p.id] : m.filter(x => x !== p.id)))
            }
          />
        );
      case 'nroProcesso':
        return (
          <Typography
            sx={{
              fontWeight: 700,
              color: C.navy,
              fontSize: 'clamp(12px, 0.68vw, 15px)',
              textDecoration: selecionado?.id === p.id ? 'underline' : 'none',
            }}
          >
            {p.nroProcesso}
          </Typography>
        );
      case 'naoTrabalhado':
        return <AlertaProcessoNaoTrabalhado ativo={p.naoTrabalhado} />;
      case 'compromisso':
        return <AlertaCompromissoProcesso compromisso={p.compromisso} hint={p.condicao} />;
      case 'requisicao':
        return <AlertaComRequisicao hint={p.requisicao} />;
      case 'pasta':
        return <AlertaPasta pasta={p.pasta} />;
      case 'valorPrincipal':
        return <Typography variant="body2">{formatBRL(p.valorPrincipal)}</Typography>;
      case 'corAlertaFase':
        return <AlertaVencimentoProcesso cor={p.corAlertaFase} dias={p.diasFase} />;
      case 'status':
        return (
          <Typography
            variant="body2"
            sx={{ fontWeight: 700, color: p.status === 'Ativo' ? C.midGreen : C.navy }}
          >
            {p.status}
          </Typography>
        );
      default:
        return (
          <Tooltip title={String(p[col.id] ?? '')} arrow>
            <Typography
              variant="body2"
              sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {p[col.id]}
            </Typography>
          </Tooltip>
        );
    }
  };

  return (
    <Box
      ref={wrapperRef}
      sx={{
        position: 'relative',
        flexShrink: 0,
        width: largura,
        minWidth: LARGURA_MIN,
        maxWidth: '100%',
      }}
    >
      <CardWrapper sx={{ pr: 2.5 }}>
        {/* Cabeçalho: título + ações em massa */}
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.5,
        }}
        >
          <Typography variant="h2">Processos</Typography>
          <AcoesEmMassaMenu idsProcessos={marcados} totalSelecionados={marcados.length} />
        </Box>

        <SearchField
          placeholder="Pesquisar pelo nº do processo"
          value={nroProcesso}
          onChange={changeNroProcesso}
          onSubmit={buscarPorNumero}
          disabled={buscandoProcesso}
          loading={buscandoProcesso}
          sx={{ mb: 1.5 }}
        />

        {/* Filtros: Status, Condição e Ordenação */}
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap',
        }}
        >
          <Typography variant="body2" sx={{ fontWeight: 700 }}>Status:</Typography>
          <FiltroMenu label="Status" valor={status} options={statusOptions} onChange={setStatus} />

          <Typography variant="body2" sx={{ fontWeight: 700, ml: 0.5 }}>Condição:</Typography>
          <FiltroMenu label="Condição" valor={condicao} options={condicaoOptions} onChange={setCondicao} multiplo />

          <FiltroMenu
            label="Ordenar por"
            valor={ordenarPor}
            options={ORDENACAO_OPTIONS}
            onChange={v => v && setOrdenarPor(v)}
            icone={<TuneIcon sx={{ fontSize: 16, color: C.navy }} />}
          />
          <Tooltip title={ordemAsc ? 'Ordem crescente' : 'Ordem decrescente'} arrow>
            <IconButton size="small" onClick={() => setOrdemAsc(v => !v)} sx={{ color: C.navy }}>
              {ordemAsc ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>

        {/* Tabela */}
        <Box sx={{ overflowX: 'hidden' }}>
          <Table size="small" sx={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                {colunasVisiveis.map(col => (
                  <TableCell
                    key={col.id}
                    align={col.align}
                    sx={{
                      width: col.min,
                      cursor: col.ordenavel ? 'pointer' : 'default',
                      color: ordenarPor === col.id ? C.navyLight : C.navy,
                    }}
                    onClick={() => {
                      if (!col.ordenavel) return;
                      if (ordenarPor === col.id) setOrdemAsc(v => !v);
                      else setOrdenarPor(col.id);
                    }}
                  >
                    {/* eslint-disable-next-line no-nested-ternary */}
                    {col.id === 'checkbox' ? (
                      <Checkbox
                        size="small"
                        checked={marcados.length > 0 && marcados.length === dados.length}
                        indeterminate={marcados.length > 0 && marcados.length < dados.length}
                        onChange={alternarTodos}
                      />
                    ) : col.hint ? (
                      <Tooltip title={col.hint} arrow>
                        <Box component="span" sx={{ display: 'inline-flex', color: C.navy }}>{col.label}</Box>
                      </Tooltip>
                    ) : (
                      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'flex-end', gap: 0.25 }}>
                        {col.label}
                        {/* eslint-disable-next-line no-nested-ternary */}
                        {ordenarPor === col.id ? (
                          ordemAsc ? <ArrowUpwardIcon sx={{ fontSize: 13 }} /> : <ArrowDownwardIcon sx={{ fontSize: 13 }} />
                        ) : null}
                      </Box>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {dados.map(p => (
                <TableRow
                  key={p.id}
                  hover
                  selected={selecionado?.id === p.id}
                  onClick={() => onSelecionar && onSelecionar(p)}
                  sx={{
                    cursor: 'pointer',
                    '&.Mui-selected': { backgroundColor: 'rgba(13,43,82,0.06)' },
                    '&.Mui-selected:hover': { backgroundColor: 'rgba(13,43,82,0.10)' },
                  }}
                >
                  {colunasVisiveis.map(col => (
                    <TableCell key={col.id} align={col.align} sx={{ width: col.min, maxWidth: col.min }}>
                      {renderCelula(p, col)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
              {dados.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={colunasVisiveis.length} align="center" sx={{ py: 4, color: C.greyish }}>
                    Nenhum processo encontrado com os filtros aplicados.
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </Box>

        {/* Rodapé */}
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1, mt: 2, pt: 1.5, borderTop: `1px solid ${C.borderGrey}`,
        }}
        >
          <BarChartIcon sx={{ fontSize: 18, color: C.navy }} />
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            Total de processos:
            {' '}
            {dados.length}
          </Typography>
          <Typography variant="caption" sx={{ color: C.warmGrey, ml: 'auto' }}>
            {colunasVisiveis.length - 1}
            {' '}
            de
            {COLUNAS.length - 1}
            {' '}
            colunas visíveis
          </Typography>
        </Box>
      </CardWrapper>

      <ResizeHandle
        largura={largura}
        min={LARGURA_MIN}
        max={larguraMax}
        onChange={setLargura}
        colunasVisiveis={colunasVisiveis.length - 1}
        colunasTotal={COLUNAS.length - 1}
      />
    </Box>
  );
}
GridProcessos.propTypes = {
  selecionado: PropTypes.object,
  onSelecionar: PropTypes.func,
  processos: PropTypes.array.isRequired,
  statusOptions: PropTypes.array.isRequired,
  condicaoOptions: PropTypes.array.isRequired,
  // Sem default no-op de propósito: um consumidor que esquecer de passar
  // esta prop deve quebrar em dev (Enter no campo chamaria undefined()),
  // não virar uma UI silenciosamente morta — mesmo padrão já usado para
  // `onChangePesquisaAtiva`/`onSucesso` em outros componentes do módulo.
  onBuscarPorNumero: PropTypes.func.isRequired,
  buscandoProcesso: PropTypes.bool,
};
GridProcessos.defaultProps = {
  selecionado: null,
  onSelecionar: null,
  buscandoProcesso: false,
};
